import { LAYOUT_TYPE, type LayoutType } from '@/constants';
import type { ElementLayout, TextStyleData } from '@/types';
import { createDefaultTextStyleData } from './dataTemplate';
import { getTitleStyles } from './titleStyles';

/** 封面标题自适应结果 */
export interface CoverTextAdaptiveResult {
  /** 调整后的字号 */
  fontSize: number;
  /** 是否启用换行 */
  wordWrap: boolean;
  /** 换行宽度（仅 wordWrap=true 时有效） */
  wordWrapWidth?: number;
  /** 处理后的文本（换行时已插入 \\n 实现平衡分行） */
  text: string;
}

/** 元素在单页内的布局比例（0-1，中心点 + 宽高占比） */
export type LayoutData = ElementLayout & {
  isFull?: boolean;
};

/** 跨页布局中单侧（左页或右页）的元素配置 */
export interface PageLayout {
  image?: LayoutData;
  text?: LayoutData;
  textStyle?: TextStyleData;
}

export interface LayoutConfigData {
  image: LayoutData[];
  text: LayoutData[];
  textStyle: TextStyleData[];
}

export const DEFAULT_COVER_FONTS: Map<string, string[]> = new Map([
  ['zh', ['cesyt-w02', 'mkwtyt']],
  ['zh_pinyin', ['toneoz-R']],
  ['en', ['pd-B']]
]);

export const DEFAULT_FONTS: Map<string, string> = new Map([
  ['zh', 'SourceHanSansCN'],
  ['zh_pinyin', 'toneoz-R'],
  ['en', 'inter-R']
]);

export const COVER_LAYOUT: LayoutConfigData = {
  image: [{ x: 0, y: 0, width: 1, height: 1, isFull: true }],
  text: [
    { x: 0, y: 0, width: 0.9, height: 0.5, bottom: 0.13, right: 0.05 },
    { x: 0, y: 0, width: 0.9, height: 0.5, top: 0.1 },
    { x: 0, y: 0, width: 0.9, height: 0.5, top: 0.37 },
    { x: 0, y: 0, width: 0.9, height: 0.5, top: 0.2, left: 0.05 },
    { x: 0, y: 0, width: 0.9, height: 0.5, top: 0.6, right: 0.05 },
    { x: 0, y: 0, width: 0.9, height: 0.5, bottom: 0.15 }
  ],
  textStyle: getTitleStyles()
};

/**
 * 全屏单画
 */
export const FULL_SCREEN_TEXT_LAYOUT: LayoutConfigData = {
  image: [{ x: 0, y: 0, width: 1, height: 1, isFull: true }],
  text: [
    { x: 0, y: 0, width: 0.6, height: 0.5, top: 0.07, left: 0.12 },
    { x: 0, y: 0, width: 0.6, height: 0.5, top: 0.07, right: 0.12 },
    { x: 0, y: 0, width: 0.6, height: 0.5, bottom: 0.07, left: 0.12 },
    { x: 0, y: 0, width: 0.6, height: 0.5, bottom: 0.07, right: 0.12 }
  ],
  textStyle: [{ font: { fontSize: 50 } }]
};

/**
 * 跨页双画1:1
 */
export const CROSS_PAGE_1_1: LayoutConfigData = {
  image: [{ x: 0, y: 0, width: 1, height: 1, isFull: true }],
  text: [
    { x: 0, y: 0, width: 0.83, height: 0.5, bottom: 0.06 },
    { x: 0, y: 0, width: 0.6, height: 0.5, top: 0.06, left: 0.12 },
    { x: 0, y: 0, width: 0.6, height: 0.5, top: 0.06, right: 0.12 },
    { x: 0, y: 0, width: 0.6, height: 0.5, bottom: 0.06, left: 0.12 },
    { x: 0, y: 0, width: 0.6, height: 0.5, bottom: 0.06, right: 0.12 }
  ],
  textStyle: [{ font: { fontSize: 50 } }]
};

/**
 * 跨域双画3:4
 */
export const CROSS_PAGE_3_4: LayoutConfigData = {
  image: [{ x: 0, y: 0, width: 1, height: 1, isFull: true }],
  text: [
    { x: 0, y: 0, width: 0.8, height: 0.5, bottom: 0.06 },
    { x: 0, y: 0, width: 0.6, height: 0.5, top: 0.07, left: 0.12 },
    { x: 0, y: 0, width: 0.6, height: 0.5, top: 0.07, right: 0.12 },
    { x: 0, y: 0, width: 0.6, height: 0.5, bottom: 0.07, left: 0.12 },
    { x: 0, y: 0, width: 0.6, height: 0.5, bottom: 0.07, right: 0.12 }
  ],
  textStyle: [{ font: { fontSize: 50 } }]
};

/**
 * 左文右图
 */
export const TEXT_IMAGE_H: LayoutConfigData = {
  image: [{ x: 0, y: 0, width: 1, height: 1, isFull: true }],
  text: [{ x: 0, y: 0, width: 0.7, height: 0.5 }],
  textStyle: [{ font: { fontSize: 50 } }]
};

/**
 * 上文下图1:1
 */
export const IMAGE_TEXT_V_1_1: LayoutConfigData = {
  image: [{ x: 0, y: 0, width: 0.73, height: 0.8, top: 0.05 }],
  text: [{ x: 0, y: 0, width: 0.78, height: 0.5, top: 0.83 }],
  textStyle: [{ font: { fontSize: 40 } }]
};

/**
 * 上文下图3:4
 */
export const IMAGE_TEXT_V_3_4: LayoutConfigData = {
  image: [{ x: 0, y: 0, width: 0.74, height: 0.8, top: 0.05 }],
  text: [{ x: 0, y: 0, width: 0.68, height: 0.5, top: 0.83 }],
  textStyle: [{ font: { fontSize: 34 } }]
};

/** LayoutType → LayoutConfigData 映射表 */
const LAYOUT_CONFIG_DATA_MAP: Partial<Record<LayoutType | 'cover', LayoutConfigData>> = {
  [LAYOUT_TYPE.FULL_SCREEN]: FULL_SCREEN_TEXT_LAYOUT,
  [LAYOUT_TYPE.CROSS_PAGE_11]: CROSS_PAGE_1_1,
  [LAYOUT_TYPE.CROSS_PAGE_34]: CROSS_PAGE_3_4,
  [LAYOUT_TYPE.IMAGE_TEXT_11]: IMAGE_TEXT_V_1_1,
  [LAYOUT_TYPE.TEXT_IMAGE_34]: TEXT_IMAGE_H,
  [LAYOUT_TYPE.IMAGE_TEXT_34]: IMAGE_TEXT_V_3_4,
  cover: COVER_LAYOUT
};

const FULL_IMAGE_TYPE: string[] = [
  LAYOUT_TYPE.IMAGE_TEXT_11,
  LAYOUT_TYPE.TEXT_IMAGE_34,
  LAYOUT_TYPE.IMAGE_TEXT_34
];
/**
 * 根据布局类型获取对应的 LayoutConfigData，
 * 并从 image 和 text 数组中各随机抽取一个返回
 */
export function getLayoutConfig(type: LayoutType | 'cover', language: string = 'zh'): PageLayout {
  const configData = LAYOUT_CONFIG_DATA_MAP[type] || FULL_SCREEN_TEXT_LAYOUT;
  const image = configData.image[Math.floor(Math.random() * configData.image.length)];
  const text = configData.text[Math.floor(Math.random() * configData.text.length)];
  const tempStyle = configData.textStyle[Math.floor(Math.random() * configData.textStyle.length)];
  const isBlack = FULL_IMAGE_TYPE.includes(type);
  const textStyle = createDefaultTextStyleData(tempStyle, isBlack);

  if (!textStyle.font?.fontFamily) {
    // 浅拷贝下来的 font 是原始对象的引用，必须创建新对象避免污染 LAYOUT_CONFIG_DATA_MAP
    textStyle.font = {
      ...textStyle.font,
      fontSize: textStyle.font?.fontSize ?? 30,
      fontFamily: getLayoutFont(language, type)
    };
  }

  if (language === 'zh_pinyin') {
    // 同理，创建新的 layout 对象防止污染原始数据
    textStyle.layout = {
      ...textStyle.layout,
      padding: Math.ceil((textStyle.font.fontSize ?? 26) * 0.5),
      lineHeight: Math.ceil((textStyle.font.fontSize ?? 26) * 1.5)
    };
  }
  return { image, text, textStyle };
}

export function getLayoutFont(lang: string, type: LayoutType | 'cover') {
  if (type === 'cover') {
    const fonts = DEFAULT_COVER_FONTS.get(lang);
    if (fonts && fonts.length > 0) {
      return fonts[Math.floor(Math.random() * fonts.length)];
    } else {
      return 'SourceHanSansCN';
    }
  } else {
    if (DEFAULT_FONTS.get(lang)) {
      return DEFAULT_FONTS.get(lang)!;
    } else {
      return 'SourceHanSansCN';
    }
  }
}

const MAX_PAGE_SIZE = 1080;

/**
 * 将 JSON 中的比例字符串（如 "16:9"）规范化为 PAGE_RATIO 格式
 */
export function normalizePageAspectRatio(ratio?: string): number {
  if (!ratio) return 16 / 9;

  const normalized = ratio.replace(':', '/');
  const [w, h] = normalized.split('/').map(Number);

  return w / h;
}

/**
 * 根据页面宽高比计算书页像素尺寸，最大边固定 1080
 */
export function parsePageDimensions(ratio: number): { width: number; height: number } {
  if (ratio >= 1) {
    return { width: MAX_PAGE_SIZE, height: Math.round(MAX_PAGE_SIZE / ratio) };
  }

  return { width: Math.round(MAX_PAGE_SIZE * ratio), height: MAX_PAGE_SIZE };
}

/**
 * 从 story.layout 字符串解析布局类型，无效时返回默认值
 */
export function resolveLayoutType(layout?: string): LayoutType {
  if (!layout) return LAYOUT_TYPE.FULL_SCREEN;

  const values = Object.values(LAYOUT_TYPE) as string[];

  if (values.includes(layout)) return layout as LayoutType;

  return LAYOUT_TYPE.IMAGE_TEXT_11;
}

/**
 * 中文封面标题自适应：根据字数计算字号与换行策略
 * - 3-6 字：保持原字号，不换行
 * - 7-10 字：自动缩小字号以适配宽度，不换行
 * - 10+ 字：自动缩小字号，拆分为两行并尽量平衡每行字数
 */
function adaptChineseCoverText(
  text: string,
  textWidth: number,
  baseFontSize: number,
  letterSpacing: number
): CoverTextAdaptiveResult {
  const charCount = [...text].length;

  // 3-6 字：保持原字号，不换行
  if (charCount <= 6) {
    return {
      fontSize: baseFontSize,
      wordWrap: false,
      text
    };
  }

  // 7-10 字：自动缩小字号（上限不超过原始字号），不换行
  if (charCount <= 10) {
    const fontSize = Math.min(baseFontSize, Math.floor(textWidth / charCount - letterSpacing));
    return {
      fontSize: Math.max(1, fontSize),
      wordWrap: false,
      text
    };
  }

  // 10 字以上：缩小字号 + 两行平衡换行
  const line1Chars = Math.ceil(charCount / 2);
  const fontSize = Math.min(baseFontSize, Math.floor(textWidth / line1Chars - letterSpacing));

  // 将字符数组按两行拆分（按 code point 边界安全截断）
  const chars = [...text];
  const line1 = chars.slice(0, line1Chars).join('');
  const line2 = chars.slice(line1Chars).join('');

  return {
    fontSize: Math.max(1, fontSize),
    wordWrap: true,
    wordWrapWidth: textWidth,
    text: `${line1}\n${line2}`
  };
}

/**
 * 英文封面标题自适应：根据单词数计算换行策略，并始终自适应字号。
 * - ≤4 个单词：不换行，自动缩小字号以适配单行宽度
 * - 4+ 个单词：拆分为两行并尽量平衡每行单词数，自动缩小字号
 *
 * 英文字符宽度约为字号的 0.5 倍（CJK 为 1.0 倍），
 * 因此字号估算公式为：fontSize = textWidth * 2 / charCount - letterSpacing
 */
function adaptEnglishCoverText(
  text: string,
  textWidth: number,
  baseFontSize: number,
  letterSpacing: number
): CoverTextAdaptiveResult {
  const words = text.split(/\s+/).filter(Boolean);

  if (words.length <= 4) {
    // ≤4 个单词：不换行，自适应字号
    const totalChars = text.length;
    const fontSize = Math.min(
      baseFontSize,
      Math.floor((textWidth / totalChars) * 2 - letterSpacing)
    );
    return {
      fontSize: Math.max(1, fontSize),
      wordWrap: false,
      text
    };
  }

  // 4+ 单词：两行平衡换行（如 5 个词 → 上 3 下 2）+ 自适应字号
  const line1WordCount = Math.ceil(words.length / 2);
  const line1 = words.slice(0, line1WordCount).join(' ');
  const line2 = words.slice(line1WordCount).join(' ');

  // 以较长行的字符数计算字号
  const maxLineChars = Math.max(line1.length, line2.length);
  const fontSize = Math.min(
    baseFontSize,
    Math.floor((textWidth / maxLineChars) * 2 - letterSpacing)
  );

  return {
    fontSize: Math.max(1, fontSize),
    wordWrap: true,
    wordWrapWidth: textWidth,
    text: `${line1}\n${line2}`
  };
}

/**
 * 根据语言和文本内容，为封面标题计算自适应字号、换行策略及分行文本。
 * - 中文：按字符数分档（3-6 / 7-10 / 10+）
 * - 英文：按单词数分档（≤4 / 4+）
 */
export function getCoverTextAdaptive(
  text: string,
  language: string,
  textWidth: number,
  baseFontSize: number,
  letterSpacing: number = 0
): CoverTextAdaptiveResult {
  if (language === 'en') {
    return adaptEnglishCoverText(text, textWidth, baseFontSize, letterSpacing);
  }
  return adaptChineseCoverText(text, textWidth, baseFontSize, letterSpacing);
}

/**
 * 将封面标题自适应结果应用到 TextStyleData 与文本内容上，
 * 返回调整后的 narration 和 textStyle，供 createTextActionData 直接使用。
 * 非 cover 布局无需调用此函数。
 */
export function applyCoverTextAdaptive(
  narration: string,
  language: string,
  textAreaWidth: number,
  textStyle?: TextStyleData
): { narration: string; textStyle: TextStyleData } {
  // 中文 titleStyles 字号 110，英文同字号会过大，等比缩小
  const rawFontSize = textStyle?.font?.fontSize ?? 110;
  const fontSize = language === 'en' ? Math.round(rawFontSize * 0.4) : rawFontSize;
  const letterSpacing = textStyle?.layout?.letterSpacing ?? 0;

  const adaptive = getCoverTextAdaptive(
    narration,
    language,
    textAreaWidth,
    fontSize,
    letterSpacing
  );

  const finalTextStyle: TextStyleData = { ...(textStyle ?? {}) };

  if (!finalTextStyle.font) {
    finalTextStyle.font = {};
  }
  finalTextStyle.font.fontSize = adaptive.fontSize;

  if (adaptive.wordWrap) {
    finalTextStyle.multiline = {
      wordWrap: true,
      wordWrapWidth: adaptive.wordWrapWidth,
      breakWords: true
    };
    narration = adaptive.text;
  }

  return { narration, textStyle: finalTextStyle };
}
